import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DateInput from './DateInput';
import { getMainAxios } from '../utils/util';
import ResearchDocViewer from './ResearchDocViewer';
import {
  page, card, cardGrid, titleRow, gridTitle, gridHint, toolbarLabel,
  btnPrimary, selectSm, emptyMsg, listRow, listRowHeader, listRowDetail, colors,
} from '../styles/fdsTheme';

interface AgentBrief {
  id: string;
  name: string;
  status: 'ok' | 'warn' | 'fail' | 'missing' | 'manual' | 'skip' | 'pending';
  judgment: string;
  issue?: string | null;
  action?: string | null;
}

interface DayRow {
  date: string;
  judgment: string;
  problemCount: number;
  warnCount: number;
  summary: string;
  agents: AgentBrief[];
}

interface HistoryRes {
  endDate: string;
  days: number;
  total: number;
  dates: DayRow[];
}

interface AgentRow {
  id: string;
  name: string;
  role: string;
  scheduled: boolean;
  skill?: string | null;
}

interface LogRes {
  id: string;
  date: string;
  content: string;
  message?: string;
}

const SCHEDULED_IDS = ['deploy-agent', 'realtime-monitor', 'ops-reviewer', 'market-brief', 'algo-report'];

const AGENT_SHORT: Record<string, string> = {
  'deploy-agent': 'deploy',
  'realtime-monitor': 'realtime',
  'ops-reviewer': 'ops',
  'market-brief': '시황',
  'algo-report': '알고',
};

const JUDGMENT_STYLE: Record<string, { bg: string; color: string; border: string }> = {
  '정상': { bg: '#ecfdf5', color: colors.down, border: colors.down },
  '문제 있음': { bg: '#fef2f2', color: colors.up, border: colors.up },
  '경미': { bg: '#fffbeb', color: '#b45309', border: '#d97706' },
  '미실행': { bg: '#f3f4f6', color: colors.hint, border: colors.hint },
  '휴장': { bg: '#eff6ff', color: '#1d4ed8', border: '#93c5fd' },
  '대기': { bg: '#f8fafc', color: colors.hint, border: '#cbd5e1' },
};

function judgmentStyle(j: string) {
  return JUDGMENT_STYLE[j] ?? JUDGMENT_STYLE['미실행'];
}

function dayBorderColor(judgment: string): string {
  return judgmentStyle(judgment).border;
}

function agentIcon(status: string): string {
  if (status === 'ok') return '✓';
  if (status === 'warn') return '△';
  if (status === 'missing' || status === 'skip' || status === 'pending') return '○';
  return '✗';
}

function agentIconColor(status: string): string {
  if (status === 'ok') return colors.down;
  if (status === 'warn') return '#d97706';
  if (status === 'missing' || status === 'skip' || status === 'pending') return colors.hint;
  return colors.up;
}

const DEFAULT_DAYS = 20;

export default function AgentDashboard() {
  const [days, setDays] = useState(DEFAULT_DAYS);
  const [endD, setEndD] = useState(new Date().toISOString().split('T')[0]);
  const [history, setHistory] = useState<DayRow[]>([]);
  const [manualRows, setManualRows] = useState<AgentRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openLog, setOpenLog] = useState<string | null>(null);
  const [logCache, setLogCache] = useState<Record<string, LogRes>>({});
  const [loadingLog, setLoadingLog] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    setOpenLog(null);
    try {
      const axios = getMainAxios();
      const [histRes, listRes] = await Promise.all([
        axios.get<HistoryRes>('/api/v1/agents/history', { params: { date: endD, days } }),
        axios.get<{ agents: AgentRow[] }>('/api/v1/agents'),
      ]);
      setHistory(histRes.data.dates ?? []);
      setManualRows((listRes.data.agents ?? []).filter(a => !a.scheduled));
    } catch (e: unknown) {
      setError(String(e));
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, [endD, days]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const loadLog = useCallback(async (agentId: string, date: string) => {
    const key = `${agentId}:${date}`;
    if (logCache[key]) return;
    setLoadingLog(key);
    try {
      const axios = getMainAxios();
      const res = await axios.get<LogRes>(`/api/v1/agents/log/${agentId}`, { params: { date } });
      setLogCache(prev => ({ ...prev, [key]: res.data }));
    } catch (e: unknown) {
      setLogCache(prev => ({
        ...prev,
        [key]: { id: agentId, date, content: '', message: String(e) },
      }));
    } finally {
      setLoadingLog(null);
    }
  }, [logCache]);

  const toggleLog = async (agentId: string, date: string, status: string) => {
    const key = `${agentId}:${date}`;
    if (openLog === key) {
      setOpenLog(null);
      return;
    }
    setOpenLog(key);
    if (status !== 'missing' && status !== 'skip' && status !== 'pending') await loadLog(agentId, date);
  };

  const scheduledCatalog = useMemo(() => SCHEDULED_IDS, []);

  return (
    <div style={page}>
      <div style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <span style={toolbarLabel}>
          Agent 일별 판정 {loading ? '(조회 중…)' : `— ${history.length}일`}
        </span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={days} onChange={e => setDays(Number(e.target.value))} style={selectSm}>
            {[10, 20, 30, 60].map(n => <option key={n} value={n}>최근 {n}일</option>)}
          </select>
          <DateInput value={endD} onChange={setEndD} style={{ fontSize: '12px' }} />
          <button type="button" onClick={fetchAll} style={btnPrimary}>조회</button>
        </div>
      </div>

      {error && <p style={{ color: colors.up, fontSize: '12px', margin: '0 0 12px' }}>{error}</p>}

      <div style={cardGrid}>
        <div style={titleRow}>
          <h3 style={gridTitle}>일별 판정</h3>
          <span style={{ color: colors.title, fontSize: '12px' }}>{history.length}건</span>
        </div>
        <p style={{ ...emptyMsg, marginTop: 0, marginBottom: 12 }}>
          로그를 읽을 필요 없음 — <b>판정 · 원인 · 조치</b>가 자동 해석됩니다. 스케줄 시각 전 미실행은 <b>대기</b>입니다. 원문 MD는 필요할 때만 펼치세요.
        </p>

        {!loading && history.length === 0 && (
          <p style={emptyMsg}>일별 기록 없음 — daily_log_review · daily_research 실행 후 sync</p>
        )}

        {history.map(day => {
          const js = judgmentStyle(day.judgment);
          const agentMap = Object.fromEntries(day.agents.map(a => [a.id, a]));
          return (
            <div key={day.date} style={listRow(dayBorderColor(day.judgment))}>
              <div style={{ ...listRowHeader, cursor: 'default', flexWrap: 'wrap', gap: 8 }}>
                <b style={{ color: colors.title, width: 88, flexShrink: 0 }}>{day.date}</b>
                <span style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  color: js.color,
                  background: js.bg,
                  border: `1px solid ${js.border}`,
                  borderRadius: 4,
                  padding: '2px 8px',
                }}>
                  {day.judgment}
                </span>
                <span style={{ color: colors.body, fontSize: '12px' }}>{day.summary}</span>
              </div>

              <div style={{ padding: '8px 12px 10px 22px' }}>
                {scheduledCatalog.map(id => {
                  const a = agentMap[id];
                  if (!a) return null;
                  const ajs = judgmentStyle(a.judgment);
                  const logKey = `${id}:${day.date}`;
                  const logOpen = openLog === logKey;
                  const log = logCache[logKey];
                  return (
                    <div key={id} style={{ marginBottom: 10 }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: '12px' }}>
                        <span style={{ color: agentIconColor(a.status), fontWeight: 700, width: 14, flexShrink: 0 }}>
                          {agentIcon(a.status)}
                        </span>
                        <span style={{ color: colors.title, fontWeight: 600, width: 56, flexShrink: 0 }}>
                          {AGENT_SHORT[id]}
                        </span>
                        <span style={{
                          color: ajs.color,
                          fontWeight: 600,
                          width: 64,
                          flexShrink: 0,
                          fontSize: '11px',
                        }}>
                          {a.judgment}
                        </span>
                        <span style={{ color: colors.body, flex: 1, lineHeight: 1.45 }}>
                          {a.issue ?? '—'}
                        </span>
                        {a.status !== 'missing' && a.status !== 'skip' && a.status !== 'pending' && (
                          <button
                            type="button"
                            onClick={() => toggleLog(id, day.date, a.status)}
                            style={{
                              fontSize: '10px',
                              color: colors.hint,
                              background: 'none',
                              border: `1px solid ${colors.divider}`,
                              borderRadius: 3,
                              padding: '2px 6px',
                              cursor: 'pointer',
                              flexShrink: 0,
                            }}
                          >
                            {logOpen ? '원문 닫기' : '원문'}
                          </button>
                        )}
                      </div>
                      {a.action && a.status !== 'ok' && (
                        <p style={{ margin: '4px 0 0 78px', fontSize: '11px', color: '#b45309', lineHeight: 1.4 }}>
                          → 조치: {a.action}
                        </p>
                      )}
                      {logOpen && (
                        <div style={{ ...listRowDetail, marginTop: 8, marginLeft: 22 }}>
                          {loadingLog === logKey && <p style={emptyMsg}>로딩…</p>}
                          {log?.message && !log.content && (
                            <p style={{ color: colors.up, fontSize: '12px' }}>{log.message}</p>
                          )}
                          {log?.content && <ResearchDocViewer content={log.content} />}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ ...card, marginTop: 16 }}>
        <div style={titleRow}>
          <h3 style={gridTitle}>
            Cursor Agent (수동)
            <span style={gridHint}>(요청 시만 · 자동 판정 없음)</span>
          </h3>
        </div>
        {manualRows.map(r => (
          <div key={r.id} style={{ ...listRow(colors.divider), marginBottom: 8 }}>
            <div style={{ ...listRowHeader, cursor: 'default' }}>
              <span style={{ width: 14 }} />
              <b style={{ color: colors.title, width: 140 }}>{r.name}</b>
              <span style={{ color: colors.body, flex: 1, fontSize: '12px' }}>{r.role}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
